import {
  InteractionRequiredAuthError,
  PublicClientApplication,
} from "@azure/msal-browser";
import Cookies from "js-cookie";
import { debounce } from "lodash";
import { endpointConstants } from "../logic/constants";
import { authHeader } from "./auth-header";
import { isMsalLogin, loginRequest, msalConfig } from "./authConfig";
import { fetchCustom } from "./fetchCustom";
import { getLocalizedString } from "./getLocalizedString";
import { history } from "./history";

var refreshingToken = false;

const msalInstance = new PublicClientApplication(msalConfig);

const fetchTyped = (endpoint, options) => {
  const account = msalInstance.getAllAccounts();

  return msalInstance
    .acquireTokenSilent({
      ...loginRequest,
      account: account[0],
    })
    .then(function (response) {
      const requestOptions = {
        method: options.type,
        headers: authHeader(response.idToken),
        body: options.body,
      };
      return fetchCustom(endpoint, requestOptions);
    })
    .catch(function (error) {
      if (
        error instanceof InteractionRequiredAuthError ||
        error?.errorCode === "login_required" ||
        error?.errorCode === "consent_required" ||
        error?.errorCode === "invalid_grant"
      ) {
        msalInstance.logoutRedirect({
          ...loginRequest,
          account: account[0],
        });
      }
      throw error;
    });
};
const refreshToken = () => {
  let refresh = Cookies.get("refreshToken");
  return fetchTyped(
    endpointConstants.REFRESHTOKEN.replace(":refreshtoken", refresh),
    {
      type: "POST",
      header: authHeader(),
      body: {},
    },
  )
    .then((response) => {
      let _token = response.body.token;
      let _refreshToken = response.body.refresh_Token;

      if (_token != null && _refreshToken != null) {
        console.log("refreshed");
        Cookies.set("token", response.body.token);
        Cookies.set("refreshToken", response.body.refresh_Token);
        refreshingToken = false;
      } else {
        Cookies.set("token", null);
        Cookies.set("refreshToken", null);
        history.push("/login");
        console.log("expired refresh token");
        refreshingToken = false;
        throw response;
      }

      return response;
    })
    .catch((error) => {
      Cookies.set("token", null);
      Cookies.set("refreshToken", null);
      refreshingToken = false;
      history.push("/login");
    });
};

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const catchErrorCode = async (error, endpoint, options) => {
  if (error.response.status === 401 && !isMsalLogin) {
    console.log("has to refresh");
    if (refreshingToken) {
      return sleep(300).then(() => callFetch(endpoint, options));
    } else {
      let token = Cookies.get("token");
      if (token != null) {
        console.log("refreshing token");
        refreshingToken = true;
        refreshToken();
        return callFetch(endpoint, options);
      } else {
        console.log("no need to refresh token");
      }
    }
  } else {
    console.log(error);
    throw Error(error);
  }
};

/** cached query that checks if the site is on maintenance. Cache  */
export const checkMaintenance = debounce(
  async () => {
    const cache = JSON.parse(sessionStorage.getItem("maintenanceConfig"));
    const now = new Date();
    let config = cache || {};
    // if cache doesn't contain data
    if (cache === null || cache?.expires < now.getTime()) {
      try {
        // load data and add it to cache
        const response = await fetchTyped(
          endpointConstants.YODACATALOGUE.MAINTENANCE,
          { type: "GET" },
        );
        config = {
          isInMaintenance: response.body,
          expires: now.getTime() + 900000,
        };
      } catch (error) {
        //in case of errors, invalidate cache
        console.log(error);
        config = {
          isInMaintenance: false,
          expires: now.getTime() + 1000,
        };
      }
      sessionStorage.setItem("maintenanceConfig", JSON.stringify(config));
    }
    // cached data
    return config;
  },
  1000,
  { leading: true },
);

const callFetch = async (endpoint, options) => {
  const page = history.location.pathname;
  const { isInMaintenance } = await checkMaintenance();
  if (isInMaintenance && page !== "/maintenance") {
    history.push({
      pathname: "/maintenance",
    });
    return Promise.reject("ONGOING MAINTENANCE");
  }
  return fetchTyped(endpoint, options)
    .then((response) => {
      if (!response.response.ok) {
        return Promise.reject(response.response.statusText);
      } else {
        return response.body;
      }
    })
    .catch((error) => {
      if (error?.response?.status === 401) {
        return catchErrorCode(error, endpoint, options);
      } else if (error?.response?.status === 404) {
        console.error("404 error:", error);
        throw new Error(getLocalizedString("ERROR_404"));
      } else {
        // Besight API errors
        if (error?.body?.message) {
          console.error("B2B API error:", error);
          return error.body;
          // MSAl random errors
        } else {
          console.error("Auth API error:", error);
          throw new Error(getLocalizedString("ERROR_AUTH"));
        }
      }
    });
};

export const get = (endpoint) => {
  return callFetch(endpoint, { type: "GET" });
};

export const post = (endpoint, body) => {
  return callFetch(endpoint, { body: JSON.stringify(body), type: "POST" });
};

export const postFile = (endpoint, file) => {
  var formData = new FormData();
  formData.append("files", file, file.name);
  return callFetch(endpoint, {
    body: formData,
    type: "POST",
    headerFile: true,
  });
};

export const put = (endpoint, body) => {
  return callFetch(endpoint, { body: JSON.stringify(body), type: "PUT" });
};

export const del = (endpoint) => {
  return callFetch(endpoint, { type: "DELETE" });
};
